import type { RHProcurementLifecycleState } from "../controllers/services/ProcurementLifecycleService"


// Shared by the server (which pages the full published set) and the webclient (which filters
// the rows it has already loaded while the server answers), so both sides decide a row's
// membership and order identically. Runtime-pure: no mongoose, no DOM.

export type RHPublishedProcurementQuickFilterKey =
    | "started"
    | "evaluating"
    | "lopetatud"
    | "inExecution"
    | "executed"
    | "favorites"

export type RHPublishedProcurementChipKey = RHPublishedProcurementQuickFilterKey | "all"

export interface RHPublishedProcurementSortingInstruction {
    keyPath: string
    dataType: string
    direction: string
}

/** Both bounds are absolute instants (ISO strings), already widened to whole days by the caller. */
export interface RHPublishedProcurementInstantRange {
    from?: string
    to?: string
}

export interface RHPublishedProcurementNumberRange {
    min?: number
    max?: number
}

export interface RHPublishedProcurementsQuery {
    searchText: string
    sortingInstructions: RHPublishedProcurementSortingInstruction[]
    quickFilterKeys: RHPublishedProcurementQuickFilterKey[]
    favoriteProcurementIds: string[]
    procuringCompanyNameText?: string
    responsibleProcuringUserSearchText?: string
    estimatedCostRange?: RHPublishedProcurementNumberRange
    publishedAtRange?: RHPublishedProcurementInstantRange
    deadlineDateRange?: RHPublishedProcurementInstantRange
    cpvCodes: string[]
}

/** The fields a row is searched, filtered and sorted by; names match the search table's row keys. */
export interface RHPublishedProcurementSearchRow {
    _id: string
    referenceNumber?: string
    title?: string
    procuringCompanyName: string
    responsibleProcuringUserName?: string
    responsibleProcuringUserEmail?: string
    publishedAt?: string
    deadlineDate?: string
    lifecycleState: RHProcurementLifecycleState
    lifecycleStateText: string
    partCostTotal: number
    cpvNumericCodes: string[]
    _rhNumberSortValue: number
    _rhNumberSortAvailability: number
}

export interface RHPublishedProcurementSearchSource {
    _id?: { toString(): string }
    referenceNumber?: string
    title?: string
    procuringCompany?: { name?: string }
    responsibleProcuringUserName?: string
    responsibleProcuringUserEmail?: string
    publishedAt?: Date | string
    deadlineDate?: Date | string
    lifecycleState: RHProcurementLifecycleState
    publishEstimatedCostOfProcurement?: boolean
    parts?: { estimatedCost?: number, CPV?: string }[]
}

export interface RHPublishedProcurementFilteredResult<Row> {
    matchingRows: Row[]
    chipCounts: Record<RHPublishedProcurementChipKey, number>
}

export const RHPublishedProcurementLifecycleStateText: Record<RHProcurementLifecycleState, string> = {
    preparing: "Ettevalmistamisel",
    started: "Alustatud",
    evaluating: "Hindamisel",
    awaitingContract: "Lõpetatud",
    closedWithoutContract: "Lõpetatud",
    inExecution: "Täitmisel",
    executed: "Teostatud",
    cancelled: "Tühistatud"
}


export class RHPublishedProcurementSearch {

    static readonly sortableKeyPaths: readonly string[] = [
        "_id", "referenceNumber", "title", "procuringCompanyName", "publishedAt", "deadlineDate",
        "lifecycleStateText", "_rhNumberSortValue", "_rhNumberSortAvailability"
    ]

    static readonly quickFilterKeys: readonly RHPublishedProcurementQuickFilterKey[] = [
        "started", "evaluating", "lopetatud", "inExecution", "executed", "favorites"
    ]


    static searchRowForProcurement(procurement: RHPublishedProcurementSearchSource): RHPublishedProcurementSearchRow {
        const parts = procurement.parts ?? []
        const costs = parts
            .map(part => part.estimatedCost)
            .filter((cost): cost is number => typeof cost === "number")
        const partCostTotal = costs.reduce((sum, cost) => sum + cost, 0)

        // The sortable cost only exists when the procurer publishes it and at least one part carries one.
        let sortableCost: number | undefined = undefined
        const finiteCosts = costs.filter(cost => Number.isFinite(cost))
        if (procurement.publishEstimatedCostOfProcurement !== false && finiteCosts.length > 0) {
            sortableCost = finiteCosts.reduce((sum, cost) => sum + cost, 0)
        }

        return {
            _id: procurement._id?.toString() ?? "",
            referenceNumber: procurement.referenceNumber,
            title: procurement.title,
            procuringCompanyName: procurement.procuringCompany?.name || "-",
            responsibleProcuringUserName: procurement.responsibleProcuringUserName,
            responsibleProcuringUserEmail: procurement.responsibleProcuringUserEmail,
            publishedAt: RHPublishedProcurementSearch._instantString(procurement.publishedAt),
            deadlineDate: RHPublishedProcurementSearch._instantString(procurement.deadlineDate),
            lifecycleState: procurement.lifecycleState,
            lifecycleStateText: RHPublishedProcurementLifecycleStateText[procurement.lifecycleState],
            partCostTotal,
            cpvNumericCodes: parts
                .map(part => part.CPV)
                .filter((cpv): cpv is string => typeof cpv === "string" && cpv.trim().length > 0)
                .map(cpv => RHPublishedProcurementSearch.cpvNumericCode(cpv.trim())),
            _rhNumberSortValue: sortableCost ?? 0,
            _rhNumberSortAvailability: sortableCost === undefined ? 0 : 1
        }
    }


    /** The plain numeric CPV code (no description, no check digit): "03110000-5 Põllukultuurid…" → "03110000". */
    static cpvNumericCode(cpvValue: string): string {
        const firstToken = cpvValue.split(" ")[0] ?? ""
        return (firstToken.split("-")[0] ?? "").trim()
    }


    /** The numeric code without trailing zeros, for hierarchical matching: "03110000-5" → "0311". */
    static cpvSignificantPrefix(cpvValue: string): string {
        return RHPublishedProcurementSearch.cpvNumericCode(cpvValue).replace(/0+$/, "")
    }


    /**
     * Comma-separated alternatives, each a space-separated list of words that must ALL appear
     * (case-insensitive substring) in the row's searchable text — the data view's local search.
     */
    static doesRowMatchSearchText(row: RHPublishedProcurementSearchRow, searchText: string): boolean {
        const alternatives = searchText.split(",").map(alternative => alternative.trim())
        if (!alternatives.some(alternative => alternative.length > 0)) {
            return true
        }
        const searchableText = RHPublishedProcurementSearch.searchableTextForRow(row).toLowerCase()
        return alternatives.some(alternative => {
            const words = alternative.toLowerCase().split(/\s+/).filter(word => word.length > 0)
            return words.length > 0 && words.every(word => searchableText.includes(word))
        })
    }


    static doesRowMatchQuickFilterKey(
        row: RHPublishedProcurementSearchRow,
        quickFilterKey: RHPublishedProcurementQuickFilterKey,
        favoriteProcurementIds: ReadonlySet<string>
    ): boolean {
        if (quickFilterKey === "favorites") {
            return favoriteProcurementIds.has(row._id)
        }
        if (quickFilterKey === "lopetatud") {
            return row.lifecycleState === "awaitingContract" || row.lifecycleState === "closedWithoutContract"
        }
        return row.lifecycleState === quickFilterKey
    }


    /** Panel criteria and CPV selection: everything except the search text and the status chips. */
    static doesRowMatchFilterCriteria(row: RHPublishedProcurementSearchRow, query: RHPublishedProcurementsQuery): boolean {
        const companyNameText = query.procuringCompanyNameText?.trim().toLowerCase() ?? ""
        if (!row.procuringCompanyName.toLowerCase().includes(companyNameText)) {
            return false
        }
        const responsibleProcuringUserSearchText = query.responsibleProcuringUserSearchText?.trim().toLowerCase() ?? ""
        if (!RHPublishedProcurementSearch.searchableTextForResponsibleProcuringUserOfProcurement(row)
            .toLowerCase().includes(responsibleProcuringUserSearchText)) {
            return false
        }
        if (!RHPublishedProcurementSearch._isNumberWithinRange(row.partCostTotal, query.estimatedCostRange)) {
            return false
        }
        if (!RHPublishedProcurementSearch._isInstantWithinRange(row.publishedAt, query.publishedAtRange)) {
            return false
        }
        if (!RHPublishedProcurementSearch._isInstantWithinRange(row.deadlineDate, query.deadlineDateRange)) {
            return false
        }
        return RHPublishedProcurementSearch._doesRowMatchCPVCodes(row, query.cpvCodes)
    }


    /**
     * Rows passing the search, panel and CPV criteria are the "base"; the chips then either narrow
     * the base (union of the selected chips) or, per chip, report how many base rows they would keep.
     */
    static filteredResultForQuery<Row extends RHPublishedProcurementSearchRow>(
        rows: Row[],
        query: RHPublishedProcurementsQuery
    ): RHPublishedProcurementFilteredResult<Row> {
        const favoriteProcurementIds = new Set(query.favoriteProcurementIds)
        const baseRows = rows.filter(row =>
            RHPublishedProcurementSearch.doesRowMatchSearchText(row, query.searchText) &&
            RHPublishedProcurementSearch.doesRowMatchFilterCriteria(row, query)
        )

        const chipCounts = { all: baseRows.length } as Record<RHPublishedProcurementChipKey, number>
        for (const key of RHPublishedProcurementSearch.quickFilterKeys) {
            chipCounts[key] = baseRows.filter(row =>
                RHPublishedProcurementSearch.doesRowMatchQuickFilterKey(row, key, favoriteProcurementIds)
            ).length
        }

        let matchingRows = baseRows
        if (query.quickFilterKeys.length > 0) {
            matchingRows = baseRows.filter(row => query.quickFilterKeys.some(key =>
                RHPublishedProcurementSearch.doesRowMatchQuickFilterKey(row, key, favoriteProcurementIds)
            ))
        }
        return { matchingRows, chipCounts }
    }


    /**
     * Same ordering as the data view's column sort: string columns compare their JSON-encoded
     * lowercase value, number columns compare numerically, ties fall to the next instruction and
     * then to the incoming order. Unknown key paths are ignored.
     */
    static rowsSortedBySortingInstructions<Row extends RHPublishedProcurementSearchRow>(
        rows: Row[],
        sortingInstructions: RHPublishedProcurementSortingInstruction[]
    ): Row[] {
        const knownInstructions = sortingInstructions.filter(instruction =>
            RHPublishedProcurementSearch.sortableKeyPaths.includes(instruction.keyPath) &&
            (instruction.dataType === "string" || instruction.dataType === "number")
        )
        if (knownInstructions.length === 0) {
            return [...rows]
        }

        const sortKeyedRows = rows.map(row => ({
            row,
            sortKeys: knownInstructions.map(instruction => {
                const value = RHPublishedProcurementSearch.sortValueForKeyPath(row, instruction.keyPath)
                if (instruction.dataType === "number") {
                    return value
                }
                return JSON.stringify(value || "").toLowerCase()
            })
        }))

        sortKeyedRows.sort((first, second) => {
            for (let index = 0; index < knownInstructions.length; index++) {
                const direction = knownInstructions[index].direction === "descending" ? -1 : 1
                const comparison = RHPublishedProcurementSearch._compareSortKeys(first.sortKeys[index], second.sortKeys[index])
                if (comparison !== 0) {
                    return comparison * direction
                }
            }
            return 0
        })
        return sortKeyedRows.map(sortKeyedRow => sortKeyedRow.row)
    }


    /** The value a sort instruction's key path names; key paths outside sortableKeyPaths have none. */
    static sortValueForKeyPath(row: RHPublishedProcurementSearchRow, keyPath: string): string | number | undefined {
        switch (keyPath) {
            case "_id":
                return row._id
            case "referenceNumber":
                return row.referenceNumber
            case "title":
                return row.title
            case "procuringCompanyName":
                return row.procuringCompanyName
            case "publishedAt":
                return row.publishedAt
            case "deadlineDate":
                return row.deadlineDate
            case "lifecycleStateText":
                return row.lifecycleStateText
            case "_rhNumberSortValue":
                return row._rhNumberSortValue
            case "_rhNumberSortAvailability":
                return row._rhNumberSortAvailability
            default:
                return undefined
        }
    }


    // Like the data view's sorter, keys of different kinds (or a missing one) simply do not order.
    private static _compareSortKeys(first: string | number | undefined, second: string | number | undefined): number {
        if (typeof first === "number" && typeof second === "number") {
            return RHPublishedProcurementSearch._ordering(first, second)
        }
        if (typeof first === "string" && typeof second === "string") {
            return RHPublishedProcurementSearch._ordering(first, second)
        }
        return 0
    }


    private static _ordering<Value extends string | number>(first: Value, second: Value): number {
        if (first < second) {
            return -1
        }
        if (first > second) {
            return 1
        }
        return 0
    }


    /** The text the data view indexes per row: each column's raw value, in column order, space-joined. */
    static searchableTextForRow(row: RHPublishedProcurementSearchRow): string {
        return [
            row.referenceNumber,
            row.title,
            row.procuringCompanyName,
            RHPublishedProcurementSearch.searchableTextForResponsibleProcuringUserOfProcurement(row),
            row._rhNumberSortValue,
            row.publishedAt,
            row.deadlineDate,
            row.lifecycleStateText,
            row._id
        ].join(" ")
    }


    static searchableTextForResponsibleProcuringUserOfProcurement(
        procurement: Pick<RHPublishedProcurementSearchSource, "responsibleProcuringUserName" | "responsibleProcuringUserEmail">
    ): string {
        return [procurement.responsibleProcuringUserName, procurement.responsibleProcuringUserEmail]
            .filter(text => typeof text === "string" && text.trim().length > 0)
            .join(" ")
    }


    private static _doesRowMatchCPVCodes(row: RHPublishedProcurementSearchRow, cpvCodes: string[]): boolean {
        if (cpvCodes.length === 0) {
            return true
        }
        // A selected branch code matches itself and every descendant, i.e. any part code under its significant prefix.
        return cpvCodes.some(cpvCode => {
            const significantPrefix = RHPublishedProcurementSearch.cpvSignificantPrefix(cpvCode)
            return significantPrefix.length === 0 ||
                row.cpvNumericCodes.some(partCode => partCode.startsWith(significantPrefix))
        })
    }


    private static _isNumberWithinRange(value: number, range?: RHPublishedProcurementNumberRange): boolean {
        if (!range) {
            return true
        }
        if (typeof range.min === "number" && value < range.min) {
            return false
        }
        return !(typeof range.max === "number" && value > range.max)
    }


    private static _isInstantWithinRange(instant: string | undefined, range?: RHPublishedProcurementInstantRange): boolean {
        if (!range) {
            return true
        }
        const time = instant ? new Date(instant).getTime() : undefined
        if (range.from !== undefined && (time === undefined || time < new Date(range.from).getTime())) {
            return false
        }
        return !(range.to !== undefined && (time === undefined || time > new Date(range.to).getTime()))
    }


    private static _instantString(value?: Date | string): string | undefined {
        if (!value) {
            return undefined
        }
        return new Date(value).toISOString()
    }

}
